import React from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";

import { i18n } from "../../translate/i18n";

import MarkdownWrapper from "../MarkdownWrapper";
import {
	formatContactDisplayName,
	getContactAvatarFallback
} from "../../utils/formatContactDisplayName";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
	},

	lastMessageTime: {
		justifySelf: "flex-end",
	},

	contactLastMessage: {
		paddingRight: 20,
	},

	newMessagesCount: {
		alignSelf: "center",
		marginRight: 8,
		marginLeft: "auto",
	},

	badgeStyle: {
		color: theme.palette.getContrastText(green[500]),
		backgroundColor: green[500],
	},

	ticketUnread: {
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(255, 255, 255, 0.09)"
				: "rgba(37, 211, 102, 0.08)",
	},

	ticketRead: {
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(255, 255, 255, 0.02)"
				: "transparent",
	},

	userTag: {
		position: "absolute",
		marginRight: 5,
		right: 5,
		bottom: 5,
		background: theme.palette.primary.main,
		color: theme.palette.getContrastText(theme.palette.primary.main),
		border: `1px solid ${theme.custom.border}`,
		padding: 1,
		paddingLeft: 5,
		paddingRight: 5,
		borderRadius: 10,
		fontSize: "0.9em"
	},
}));

const TicketListItem = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const { ticketId } = useParams();
	const contactDisplayName = formatContactDisplayName(ticket?.contact);
	const unreadMessages = Number(ticket?.unreadMessages || 0);
	const isUnread = unreadMessages > 0;

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onClick={e => {
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				className={`${classes.ticket} ${
					isUnread ? classes.ticketUnread : classes.ticketRead
				}`}
			>
				<ListItemAvatar>
					<Avatar src={ticket?.contact?.profilePicUrl}>
						{getContactAvatarFallback(ticket?.contact)}
					</Avatar>
				</ListItemAvatar>
				<ListItemText
					disableTypography
					primary={
						<span className={classes.contactNameWrapper}>
							<Typography
								noWrap
								component="span"
								variant="body2"
								color="textPrimary"
							>
								{contactDisplayName}
							</Typography>
							{ticket.lastMessage && (
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
										<>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
									) : (
										<>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
									)}
								</Typography>
							)}
							{ticket.whatsappId && (
								<div className={classes.userTag} title={i18n.t("ticketsList.connectionTitle")}>{ticket.whatsapp?.name}</div>
							)}
						</span>
					}
					secondary={
						<>
							<span className={classes.contactNameWrapper}>
								<Typography
									className={classes.contactLastMessage}
									noWrap
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{ticket.lastMessage ? (
										<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
									) : (
										<br />
									)}
								</Typography>

								<Badge
									className={classes.newMessagesCount}
									badgeContent={unreadMessages}
									classes={{
										badge: classes.badgeStyle,
									}}
								/>
							</span>
						</>
					}
				/>
			</ListItem>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

const areEqual = (prevProps, nextProps) => {
	const prevTicket = prevProps.ticket;
	const nextTicket = nextProps.ticket;

	if (prevTicket === nextTicket) {
		return true;
	}

	return (
		prevTicket.id === nextTicket.id &&
		prevTicket.updatedAt === nextTicket.updatedAt &&
		prevTicket.unreadMessages === nextTicket.unreadMessages &&
		prevTicket.lastMessage === nextTicket.lastMessage &&
		prevTicket.status === nextTicket.status &&
		prevTicket.userId === nextTicket.userId &&
		prevTicket.queueId === nextTicket.queueId &&
		prevTicket.whatsappId === nextTicket.whatsappId &&
		prevTicket.contact?.name === nextTicket.contact?.name &&
		prevTicket.contact?.profilePicUrl === nextTicket.contact?.profilePicUrl
	);
};

export default React.memo(TicketListItem, areEqual);
